import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import HiringService, { ImportRowResult, ImportSummary } from '../../api/services/hiring.service';
import { parseCsvToObjects, toCsv, downloadCsv } from '../../utils/csv';
import { STAGE_LABEL, OUTCOME_LABEL } from './hiringUI';

// Bringing a hiring spreadsheet in.
//
// Same shape as the team importer in Users, deliberately: download a
// template, choose a file, check it, then import. Anyone who has done one
// should not have to learn the other.
//
// What is different is what "check it" means here. A candidate row carries
// a status, fifteen checkpoint columns and somebody's SSN, and the
// decisions the importer makes about those are not obvious — so the
// preview reports them in words rather than just counting rows.

/**
 * Header spellings accepted in an uploaded file.
 *
 * Two audiences at once. The friendly headers our own template ships with,
 * which is the path anybody arriving here should take; and the raw column
 * names from ONE specific Google Sheet — the tracker this product replaced
 * — so that migration needed no retyping.
 *
 * Be careful what the UI promises about the second group. The copy used to
 * say an export of "an existing tracker" works untouched, which read as a
 * general claim and is not one: these aliases were written against the
 * column names of a single sheet. Somebody else's export shares almost
 * none of them, so they get a file where every row is skipped for want of
 * a name. The template is the supported route; this is a convenience for a
 * sheet we happen to know.
 *
 * Matching ignores case, spaces, underscores and dashes (see
 * parseCsvToObjects).
 */
const FIELD_ALIASES: Record<string, string> = {
  // identity
  name: 'name', fullname: 'name', candidate: 'name', candidatename: 'name',
  email: 'email', emailaddress: 'email',
  phone: 'phone', mobile: 'phone', mobilenumber: 'phone', phonenumber: 'phone',
  status: 'status', stage: 'status',
  recruiter: 'recruiter', source: 'recruiter', wheretheycamefrom: 'recruiter',
  entity: 'entity',

  // outreach and interview
  hellosent: 'hello_sent', outreachsent: 'hello_sent', outreach: 'hello_sent',
  replyback: 'reply_back', interviewheld: 'reply_back', interviewstatus: 'reply_back',
  payapproved: 'pay_approved', payrate: 'pay_approved', offeredrate: 'pay_approved',

  // screening
  bgcdone: 'bgc_done', bgc: 'bgc_done', backgroundcheck: 'bgc_done',
  drugdone: 'drug_done', drugscreen: 'drug_done', drugtest: 'drug_done',
  mecexpiry: 'mec_expiry', mecexpiration: 'mec_expiry', mecexpires: 'mec_expiry',

  // training
  certuploaded: 'cert_uploaded', safetycertificate: 'cert_uploaded', cert: 'cert_uploaded',
  roadtestuploaded: 'road_test_uploaded', roadtest: 'road_test_uploaded',

  // onboarding
  onboardingdocs: 'onboarding_docs', onboardingpacket: 'onboarding_docs',
  fedexpw: 'fedex_pw', fedexpassword: 'fedex_pw',
  sig: 'sig',
  everify: 'everify', everifycase: 'everify',
  fedexid: 'fedex_id',
  intromanagers: 'intro_managers', introtomanagers: 'intro_managers',
  beansaccount: 'beans_account', beans: 'beans_account',
  fuelcard: 'fuel_card',
  hiringdate: 'hiring_date',

  // driver details
  dlnum: 'dl_num', licensenumber: 'dl_num', licencenumber: 'dl_num',
  dlstate: 'dl_state', licensestate: 'dl_state', licencestate: 'dl_state',
  dlexp: 'dl_exp', licenseexpiry: 'dl_exp', licenceexpiry: 'dl_exp', licenseexpires: 'dl_exp',
  bday: 'bday', birthday: 'bday', dateofbirth: 'bday',
  ssn: 'ssn', ssnlast4: 'ssn',
  address: 'address',
  citizen: 'citizen', uscitizen: 'citizen',
  sizes: 'sizes', shirtpantssize: 'sizes', shirttrousersize: 'sizes', uniformsize: 'sizes',
  startdate: 'start_date',
  notes: 'notes',
  lastupdated: 'last_updated',

  // The old tracker's own bookkeeping. Mapped so a straight sheet download
  // does not report them as unrecognised columns; the importer ignores them.
  id: 'id', num: 'num', interview: 'interview',
};

// The template ships with friendly headers, and the aliases above accept
// them back. Order runs left to right the way the pipeline does.
const TEMPLATE_HEADERS = [
  'Name', 'Email', 'Mobile number', 'Status', 'Where they came from',
  'Outreach sent', 'Interview', 'Pay approved',
  'Background check', 'Drug screen', 'MEC expiry',
  'Safety certificate', 'Road test',
  'Onboarding docs', 'FedEx password', 'SIG', 'eVerify', 'FedEx ID',
  'Intro to managers', 'Beans account', 'Fuel card', 'Hiring date',
  'License number', 'License state', 'License expiry', 'Birthday', 'Address',
  'Shirt/trouser size', 'Notes',
];

// The row key each template column maps to, in the same order. Kept
// beside the headers rather than derived from the alias map, which is
// many-to-one and has no meaningful order.
const TEMPLATE_KEYS = [
  'name', 'email', 'phone', 'status', 'recruiter',
  'hello_sent', 'reply_back', 'pay_approved',
  'bgc_done', 'drug_done', 'mec_expiry',
  'cert_uploaded', 'road_test_uploaded',
  'onboarding_docs', 'fedex_pw', 'sig', 'everify', 'fedex_id',
  'intro_managers', 'beans_account', 'fuel_card', 'hiring_date',
  'dl_num', 'dl_state', 'dl_exp', 'bday', 'address',
  'sizes', 'notes',
];

// Row 1 shows the vocabulary on a candidate part-way through. Row 2 shows
// the other truth about this file: a first name and a phone number is a
// perfectly good row, and most leads arrive as exactly that.
const TEMPLATE_EXAMPLE = [
  ['Dana Reyes', 'dana.reyes@example.com', '(901) 555-0198', 'Screening', 'Indeed',
    'Replied', 'Done', '$21.50/hr',
    'Completed', 'Processing', '5/15/2027',
    'Not Started', 'Not Done',
    'false', '', 'false', '', '',
    'false', 'false', 'false', '',
    'D9911237', 'TN', '3/9/2029', '7/14/1991', '114 Poplar Ave, Memphis TN',
    'L / 34x32', 'Strong interview. Wants weekends off.'],
  ['Marcus Webb', '', '9015550141', 'New Lead', 'Referral — Ahmet',
    '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '',
    '', '', '', '', '', '', ''],
];

const STATUS_WORDS = [
  'New Lead', 'Contacted', 'Replied', 'Interviewed', 'Pay Approved', 'Screening',
  'Training', 'Onboarding', 'Ready to Start', 'On Hold', 'Hired', 'Active',
  'No Response', 'No Show', 'Inactive', 'Disqualified', 'Fired', 'Withdrew',
];

const ImportCandidatesDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}> = ({ open, onClose, onImported }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [results, setResults] = useState<ImportRowResult[] | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [checked, setChecked] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setFileName(''); setRows([]); setUnknownHeaders([]);
    setResults(null); setSummary(null);
    setChecked(false); setCommitted(false); setBusy(false); setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (busy) return;
    if (committed) onImported();
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setResults(null); setSummary(null);
    setChecked(false); setCommitted(false);
    try {
      const parsed = parseCsvToObjects(await file.text(), FIELD_ALIASES);
      if (parsed.rows.length === 0) {
        setError('That file has no data rows. Download the template to see the expected columns.');
        return;
      }
      setFileName(file.name);
      setRows(parsed.rows);
      setUnknownHeaders(parsed.unknownHeaders);
    } catch (err: any) {
      setError(`Could not read that file: ${err.message}`);
    }
  };

  const send = async (dryRun: boolean) => {
    setBusy(true);
    setError('');
    try {
      const response = await HiringService.import(rows, dryRun);
      setResults(response.results);
      setSummary(response.summary);
      if (dryRun) setChecked(true); else setCommitted(true);
    } catch (err: any) {
      // Read `error` as well as `message`. The role middlewares answer with
      // `{ error }` and only `message` was being read, so a 403 surfaced as
      // the generic sentence below — "The import failed. Nothing was
      // written." on a dry run, which writes nothing either way, sent the
      // reader looking at their spreadsheet for a problem in the router.
      //
      // The 403 wording matters: hiring refuses the SA role on purpose
      // (candidates carry compliance data, which is the customer's, not the
      // platform's), and the remedy is the account switcher in the topbar,
      // not a role change.
      const body = err?.response?.data;
      const status = err?.response?.status;
      setError(
        body?.message
        || body?.error
        || (status === 403
          ? 'This account cannot work the pipeline. If you also hold an admin '
            + 'account on this company, switch to it from the topbar.'
          : 'The import failed. Nothing was written.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () =>
    downloadCsv('toposds-candidate-template.csv', toCsv(TEMPLATE_HEADERS, TEMPLATE_EXAMPLE));

  // The rows that did not land, with their original cells, so the file can
  // be corrected and uploaded again rather than retyped. Re-uploading the
  // whole thing is safe too — anyone already imported is caught as a
  // duplicate and skipped.
  const downloadSkipped = () => {
    const out = (results || [])
      .filter((r) => !r.ok)
      .map((r) => {
        const original = rows[r.row] || {};
        return [...TEMPLATE_KEYS.map((key) => original[key] ?? ''), r.message];
      });
    downloadCsv('toposds-candidates-not-imported.csv', toCsv([...TEMPLATE_HEADERS, 'why'], out));
  };

  const skippedCount = (results || []).filter((r) => !r.ok).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import candidates from a spreadsheet</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!rows.length && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Start with the template.</strong> Download it, paste your list into it, and
              upload it here. It has the columns this page reads, so nothing gets dropped for being
              named something else.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              A CSV you already have may work as it is — the check below tells you before anything
              is written, and never costs you more than a re-upload. If it comes back with rows
              skipped, move the list into the template rather than renaming columns to guess.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Only a name is needed, or a phone number if you do not have one — a lead is often
              nothing more than that. Everything else is optional and can be filled in later.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              The <strong>Status</strong> column is read if it holds one of the words a hiring
              tracker normally uses: {STATUS_WORDS.slice(0, 9).join(', ')}, and the ones for people
              who did not make it — {STATUS_WORDS.slice(9).join(', ')}. Anything else imports as a
              new lead.
            </Typography>

            <Link component="button" type="button" onClick={downloadTemplate} sx={{ mb: 3, display: 'inline-block' }}>
              Download the template
            </Link>

            <Box>
              <Button variant="contained" component="label" startIcon={<UploadIcon />}>
                Choose CSV file
                <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
              </Button>
            </Box>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>{fileName}</strong> — {rows.length} row{rows.length === 1 ? '' : 's'}
            </Typography>

            {unknownHeaders.length > 0 && (
              <Alert severity="warning" sx={{ my: 2 }}>
                These columns were not recognised and will be ignored: {unknownHeaders.join(', ')}
              </Alert>
            )}

            {!checked && !committed && (
              <Alert severity="info" sx={{ my: 2 }}>
                Nothing is written yet. Check the file first — every row is worked out in full
                against your company before a single candidate is created.
              </Alert>
            )}

            {busy && <LinearProgress sx={{ my: 2 }} />}

            {summary && (
              <>
                {summary.missingColumns.length > 0 && (
                  <Alert severity="warning" sx={{ my: 2 }}>
                    <Typography variant="body2" fontWeight={700} gutterBottom>
                      This file is missing columns a hiring sheet usually has
                    </Typography>
                    {summary.missingColumns.map((m) => (
                      <Typography key={m.column} variant="body2">
                        <strong>{m.column}</strong> — {m.cost}
                      </Typography>
                    ))}
                    <Typography variant="caption" color="text.secondary">
                      If this came from a tracker’s own “Export CSV” button, download the sheet
                      itself instead — the button leaves several columns out.
                    </Typography>
                  </Alert>
                )}

                <Stack direction="row" gap={1} sx={{ my: 2 }} flexWrap="wrap" alignItems="center">
                  <Chip
                    color="success" size="small"
                    label={`${summary.written ?? summary.ready} ${committed ? 'imported' : 'ready'}`}
                  />
                  {summary.skipped > 0 && (
                    <Chip color="default" size="small" variant="outlined" label={`${summary.skipped} skipped`} />
                  )}
                  {skippedCount > 0 && (
                    <Button size="small" startIcon={<DownloadIcon />} onClick={downloadSkipped}>
                      Download the rows that were left out
                    </Button>
                  )}
                </Stack>

                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  {Object.entries(summary.byStage).map(([stage, count]) => (
                    <Chip
                      key={stage} size="small" variant="outlined"
                      label={`${STAGE_LABEL[stage as never] || stage}: ${count}`}
                    />
                  ))}
                </Stack>

                {/* The decisions worth stating in words, because none of
                    them is obvious from a row count. */}
                <Stack gap={1} sx={{ mb: 2 }}>
                  {summary.ssnsDropped > 0 && (
                    <Alert severity="info">
                      <strong>{summary.ssnsDropped}</strong> full Social Security number
                      {summary.ssnsDropped === 1 ? ' was' : 's were'} dropped — only the last four
                      digits are kept. The full number belongs on the I-9, which is filed against
                      somebody once they are an employee.
                    </Alert>
                  )}
                  {summary.unbackedDocuments > 0 && (
                    <Alert severity="warning">
                      <strong>{summary.unbackedDocuments}</strong> checkpoint
                      {summary.unbackedDocuments === 1 ? '' : 's'} the sheet called complete
                      {summary.unbackedDocuments === 1 ? ' is' : ' are'} imported as{' '}
                      <strong>in progress</strong>. Those are the ones backed by a document, and
                      the spreadsheet held a tick rather than the file. Upload the file and the
                      checkpoint turns green on its own.
                    </Alert>
                  )}
                  {summary.backfilled > 0 && (
                    <Alert severity="info">
                      <strong>{summary.backfilled}</strong> earlier step
                      {summary.backfilled === 1 ? '' : 's'} recorded as done because the status in
                      the file says they happened. Each one says so on the candidate — no record of
                      who did it or when survives the import.
                    </Alert>
                  )}
                  {summary.passwordsDropped > 0 && (
                    <Alert severity="info">
                      <strong>{summary.passwordsDropped}</strong> FedEx password
                      {summary.passwordsDropped === 1 ? '' : 's'} in that file
                      {summary.passwordsDropped === 1 ? ' was' : ' were'} not carried across. The
                      column held real credentials in plain text; the checkpoint records that a
                      password was set, and the password itself stays out of TopoSDS.
                    </Alert>
                  )}
                  {summary.mergedDuplicates?.length > 0 && (
                    <Alert severity="info">
                      <strong>{summary.mergedDuplicates.length}</strong> {summary.mergedDuplicates.length === 1 ? 'person appears' : 'people appear'} more than
                      once. The row that got furthest was kept and the others skipped —
                      usually the one that ended in a hire.
                    </Alert>
                  )}
                  {summary.sharedContacts?.length > 0 && (
                    <Alert severity="warning">
                      <Typography variant="body2" fontWeight={700} gutterBottom>
                        Same phone number, different names
                      </Typography>
                      {summary.sharedContacts.map((line) => (
                        <Typography key={line} variant="body2">{line}</Typography>
                      ))}
                      <Typography variant="caption" color="text.secondary">
                        Both were imported rather than merged — a shared phone is not proof of the
                        same person, and merging would delete somebody.
                      </Typography>
                    </Alert>
                  )}
                  {summary.matchedHires.length > 0 && (
                    <Alert severity="success">
                      <strong>{summary.matchedHires.length}</strong> row
                      {summary.matchedHires.length === 1 ? '' : 's'} matched somebody who already
                      works here and will be linked to their record:{' '}
                      {summary.matchedHires.join(', ')}.
                    </Alert>
                  )}
                  {summary.unmatchedHires.length > 0 && (
                    <Alert severity="warning">
                      <strong>{summary.unmatchedHires.length}</strong> row
                      {summary.unmatchedHires.length === 1 ? '' : 's'} say they were hired, but
                      nobody here matches: {summary.unmatchedHires.join(', ')}. They import as
                      still open — no account is created and no invitation is sent. Hire or link
                      them once you have checked.
                    </Alert>
                  )}
                </Stack>

                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results!.map((r) => (
                        <TableRow key={`${r.row}-${r.name}`}>
                          {/* +2: one for the header line, one because
                              spreadsheets count from 1. */}
                          <TableCell>{r.row + 2}</TableCell>
                          <TableCell>{r.name || <em>blank</em>}</TableCell>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color={r.ok ? 'text.primary' : 'text.secondary'}>
                              {r.ok && r.stage
                                ? `${STAGE_LABEL[r.stage]}${r.outcome && r.outcome !== 'open' ? ` · ${OUTCOME_LABEL[r.outcome]}` : ''}`
                                : r.message}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {committed && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Done. Nothing was emailed and no accounts were created — imported candidates are on
                the board now.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy}>{committed ? 'Close' : 'Cancel'}</Button>
        {rows.length > 0 && !committed && !checked && (
          <Button variant="outlined" onClick={() => send(true)} disabled={busy}>
            Check the file
          </Button>
        )}
        {checked && !committed && (
          <Button variant="contained" onClick={() => send(false)} disabled={busy || summary?.ready === 0}>
            Import {summary?.ready ?? 0} candidate{summary?.ready === 1 ? '' : 's'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportCandidatesDialog;
